import { ToolbarProps } from "elements/base/provider";
import { MondayTaskElement } from "../schema";
import { ButtonGroup } from "react-bootstrap";
import { produceWithPatches } from "immer";
import { MondayTaskColorPicker } from "./monday-task-color-picker";
import { MondayTaskColumnPicker } from "./column-picker/monday-task-column-picker";
import { MondayTaskCustomizeButton } from "./monday-task-customize";
import { MondayItemManagerProvider } from "frontend/canvas-designer-new/integrations/components/monday-item-manager-context";
import { IntegrationsProvider } from "frontend/hooks/use-integrations-provider";

export default function MondayTaskToolbar({ ids, elements, context }: ToolbarProps<MondayTaskElement>) {
  const { reflect, undoRedoStack, documentId } = context;

  if (elements.length === 0) {
    return null;
  }

  const element = elements[0];

  // Hide the toolbar if an element is inside a gantt chart.
  if (elements.some((element) => element.containerId?.startsWith("gantt-"))) {
    return null;
  }

  function handleColorChange(value: { fill: string | null; colorBy: string | null }, addUndo: boolean) {
    const changes = ids.map((id, index) => {
      const currentElement = elements[index];
      const [_, patch, inverse] = produceWithPatches(currentElement, (draft: MondayTaskElement) => {
        draft.fill = value.fill ?? draft.fill;
        draft.colorBy = value.colorBy;
      });
      return { id, patch, inverse };
    });

    const doPatches = changes.filter(({ patch }) => patch.length > 0).map(({ id, patch }) => ({ id, patch }));
    if (doPatches.length === 0) {
      return;
    }

    const undoPatches = changes.map(({ id, inverse }) => ({ id, patch: inverse }));

    if (addUndo) {
      undoRedoStack?.addAction({
        do: () => {
          reflect?.mutate.patchCanvasEl({ changes: doPatches });
        },
        undo: () => {
          reflect?.mutate.patchCanvasEl({ changes: undoPatches });
        },
      });
    } else {
      reflect?.mutate.patchCanvasEl({ changes: doPatches });
    }
  }

  function handleColumnsChange(
    value: { hiddenColumns?: string[]; pinnedColumns?: string[] },
    addUndo: boolean,
    metadata?: { shouldExpand?: boolean; shouldCollapse?: boolean }
  ) {
    const changes = ids.map((id, index) => {
      const currentElement = elements[index];
      const [_, patch, inverse] = produceWithPatches(currentElement, (draft: MondayTaskElement) => {
        if (value.hiddenColumns !== undefined) {
          draft.hiddenColumns = value.hiddenColumns;
        }
        if (value.pinnedColumns !== undefined) {
          draft.pinnedColumns = value.pinnedColumns;
        }

        if (metadata?.shouldExpand) {
          draft.expanded = true;
        } else if (metadata?.shouldCollapse) {
          draft.expanded = false;
        }
      });
      return { id, patch, inverse };
    });

    const doPatches = changes.filter(({ patch }) => patch.length > 0).map(({ id, patch }) => ({ id, patch }));
    if (doPatches.length === 0) {
      return;
    }

    const undoPatches = changes.map(({ id, inverse }) => ({ id, patch: inverse }));

    if (addUndo) {
      undoRedoStack?.addAction({
        do: () => {
          reflect?.mutate.patchCanvasEl({ changes: doPatches });
        },
        undo: () => {
          reflect?.mutate.patchCanvasEl({ changes: undoPatches });
        },
      });
    } else {
      reflect?.mutate.patchCanvasEl({ changes: doPatches });
    }
  }

  return (
    // These two providers are here since the toolbar is rendered in a portal
    // and does not have access to the providers in the canvas designer index.
    <IntegrationsProvider documentId={documentId}>
      <MondayItemManagerProvider documentId={documentId}>
        {elements.length === 1 && (
          <MondayTaskCustomizeButton element={element} elementId={ids[0]} documentId={documentId} />
        )}

        {elements.length === 1 && (
          <ButtonGroup>
            <MondayTaskColumnPicker element={element} documentId={documentId} onChange={handleColumnsChange} />
          </ButtonGroup>
        )}

        <ButtonGroup>
          <MondayTaskColorPicker
            elements={elements}
            documentId={documentId}
            onChange={handleColorChange}
            multi={elements.length > 1}
          />
        </ButtonGroup>
      </MondayItemManagerProvider>
    </IntegrationsProvider>
  );
}
